import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, switchMap, catchError, of, from, map, firstValueFrom } from 'rxjs';
import { environment } from '@environments';
import { SKIP_LOADER_HEADER } from '@constants';
import { StartApplicationResponse } from '@shared/interfaces/start.interface';
import { RecaptchaService } from './recaptcha.service';
import { CacheManagerService } from './cache-manager.service';
import { isNullOrUndefined } from '@utils/predicates';

const AUTH_TOKEN_KEY = 'auth_token';
const START_URL = `${environment.apiPath}api/onboarding/${environment.version.toolbox}/start`;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly recaptcha = inject(RecaptchaService);
  private readonly cache = inject(CacheManagerService);

  /**
   * Ejecuta reCAPTCHA + POST /start y guarda el token.
   * Devuelve una Promise para poder usarse como APP_INITIALIZER.
   */
  initialize(): Promise<void> {
    if (!environment.isStart) {
      return Promise.resolve();
    }

    return firstValueFrom(
      from(this.recaptcha.execute('startApplication')).pipe(
        switchMap((captcha) => this.startApplication(captcha)),
        catchError(() => of(null))
      )
    ).then(() => {});
  }

  startApplication(recaptchaToken: string): Observable<StartApplicationResponse> {
    const headers = new HttpHeaders().set(SKIP_LOADER_HEADER, 'true');
    return this.http
      .post<StartApplicationResponse>(START_URL, { recaptcha: recaptchaToken }, { headers })
      .pipe(
        tap((response) => {
          if (response?.initialData?.token) {
            this.setToken(response.initialData.token);
          }
        })
      );
  }

  getToken(): string | null {
    return this.cache.get<string>(AUTH_TOKEN_KEY);
  }

  hasToken(): boolean {
    return !isNullOrUndefined(this.getToken());
  }

  ensureToken(): Observable<void> {
    if (this.hasToken()) {
      return of(undefined);
    }
    return from(this.initialize()).pipe(map(() => undefined));
  }

  private setToken(token: string): void {
    this.cache.set(AUTH_TOKEN_KEY, token);
  }

  clearToken(): void {
    this.cache.remove(AUTH_TOKEN_KEY);
  }
}
